import { useTopMessage } from '@/hooks/query/use-account-messages';
import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { NotifIcon } from '@/components/icons/svg-icons';
import clsx from 'clsx';
import { homeStyle } from './home-page.style';

const VerticalContentMarquee = ({ content }: {content: string}) => {
	const contentRef = useRef<HTMLDivElement>(null);
	const marqueeRef = useRef<HTMLDivElement>(null);
	const [contentHeight, setContentHeight] = useState<number>(0);
	const [marqueeHeight, setMarqueeHeight] = useState<number>(0);
	const [animateArray, setAnimateArray] = useState<number[]>([]);
	const [animateDuration, setAnimateDuration] = useState<number>(0);

	const generateAnimateArray = (marqueeHeight: number, contentHeight: number) => {
		const height = marqueeHeight / 2;
		const array = [0, 0];
		for (let i = 0; i < height / contentHeight; i++) {
			array.push(-(i + 1) * contentHeight, -(i + 1) * contentHeight);
		}
		return array;
	}

	const generateTimesArray = (index: number) => {
		const array = [];
		const step = 1 / (index - 1);
		for (let i = 0; i <= index - 1; i++) {
			array.push(step * i);
		}
		return array;
	}

	useEffect(() => {
		if (!marqueeRef.current) return;
		if (!contentRef.current) return;

		setContentHeight(contentRef.current?.offsetHeight);
		setMarqueeHeight(marqueeRef.current?.offsetHeight);
		setAnimateArray(generateAnimateArray(marqueeHeight, contentHeight));
		setAnimateDuration((marqueeHeight / contentHeight) * 2);
	}, [contentRef.current, marqueeRef.current]);

	const marqueeVariants = {
		animate: {
			y: animateArray,
			transition: {
				y: {
					repeat: Infinity,
					repeatType: 'loop',
					duration: animateDuration,
					ease: 'linear',
					times: generateTimesArray(animateArray.length),
				},
			},
		},
	}
	return (
		<div className="home-notif__content" ref={contentRef}>
			{
				!content ? null : (
					<motion.div
						className={clsx('home-notif__marquee', homeStyle.marquee)}
						variants={marqueeVariants}
						animate="animate"
						ref={marqueeRef}
					>
						<span>{content}</span>
						<span>{content}</span>
					</motion.div>
				)
			}
		</div>
	)
};
export const MobileNotification = () => {
	const { messagesInfo } = useTopMessage();
	const notificationRef = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState<number>(0);
	const statusRestoreTimeout = useRef<ReturnType<typeof setTimeout>>();
	const [isResizing, setIsResizing] = useState<boolean>(false);
	const [messageContent, setMessageContent] = useState<string>('');
	const getNotificationWidth = (): number => {
		if (!notificationRef.current) {
			return 0;
		}
		const notificationDom = notificationRef.current;
		return notificationDom.getBoundingClientRect().width;
	};
	
	const delayedResizingRestore = () => {
		clearTimeout(statusRestoreTimeout.current);
		statusRestoreTimeout.current = setTimeout(() => {
			const currentWidth = getNotificationWidth();
			if (currentWidth !== width) {
				setWidth(currentWidth);
			}
			setIsResizing(false);
		}, 100);
	};
	const resizeHandler = () => {
		setIsResizing(true);
		delayedResizingRestore();
	};
	useEffect(() => {
		setMessageContent(messagesInfo?.content ?? '');
	}, [messagesInfo]);
	useEffect(() => {
		window.addEventListener('resize', resizeHandler);
		return () => {
			clearTimeout(statusRestoreTimeout.current);
			window.removeEventListener('resize', resizeHandler);
		}
	}, [])
	return (
		<div className="home-notif" ref={notificationRef}>
			<div className={clsx('home-notif__icon', homeStyle.notifyIcon)}>
				<NotifIcon />
			</div>
			{
				!messageContent ? null : 
					isResizing  ? (
						<div className="home-notif__content">
							<div className={clsx('home-notif__marquee', homeStyle.marquee)}>
								<span>{messageContent}</span>
							</div>
						</div>
					): (
						<VerticalContentMarquee content={messageContent} />
					)
			}
		</div>
	);
};